import type { DataLayerEvent } from '@marriott/mi-headless-utils';
import { ConsentOptionPayload } from '../../organisms/WebPromptConsent/WebPromptConsent.types';
import { webPromptConsentACDLkeyGenerator } from './acdlKeyGenerator';
import { WEB_PROMPT_EXPERIENCE_VARIANTS } from './constants/constants';

// Utility to map raw consent option to ConsentOptionPayload
type RawConsentOption = {
  method?: { code?: string } | string;
  type?: { code?: string } | string;
  subscribed?: boolean;
};
export function mapToConsentOptionPayload(opt: RawConsentOption): ConsentOptionPayload {
  return {
    method: { code: typeof opt.method === 'object' ? opt.method?.code || '' : (opt.method as string) || '' },
    type: { code: typeof opt.type === 'object' ? opt.type?.code || '' : (opt.type as string) || '' },
    subscribed: !!opt.subscribed,
  };
  }

  /**
   * Helper to push ACCOUNT_UPDATE datalayer event for WebPrompt flows.
   * @param updateDatalayer - The updateDatalayer function from useSession
   * @param options - Consent options array
   * @param phoneExists - 'Y' or 'N' for phone existence
   */

export interface PushWebPromptDatalayerArgs {
  updateDatalayer: (payload: DataLayerEvent) => void;
  options: ConsentOptionPayload[];
  phoneExists: 'Y' | 'N';
  eventName: string;
  modal?: boolean; // Optional, defaults to true
}

/**
 * Generic helper to push datalayer event for WebPrompt flows.
 * Includes user.settings_preferences only for ACCOUNT_UPDATE events.
 */

export function pushWebPromptDatalayer(args: PushWebPromptDatalayerArgs) {
  const { updateDatalayer, options, phoneExists, eventName, modal = true } = args;
  const { emailConsent, smsConsent, postalConsent } = getConsentStatusFromOptions(options);
  const consentString = getConsentDataLayerString({ phoneExists, emailConsent, smsConsent, postalConsent });
  const experience = getExperienceVariant(phoneExists, emailConsent, smsConsent, postalConsent);
  let datalayerObj: DataLayerEvent = {
    event: eventName,
    page: { experience, modal },
  };
  if (eventName === 'ACCOUNT_UPDATE') {
    datalayerObj = {
      event: eventName,
      ...webPromptConsentACDLkeyGenerator(consentString, experience, modal),
    };
  }
  updateDatalayer(datalayerObj);
}

export function getExperienceVariant(
  phoneExists: string,
  emailConsent: string,
  smsConsent: string,
  postalConsent: string
) {
  const phone = phoneExists === 'Y';
  const consents = [emailConsent, smsConsent, postalConsent];
  const allConsentsY = consents.every(c => c === 'Y');
  const anyConsentN = consents.some(c => c === 'N');
  if (!phone && anyConsentN) return WEB_PROMPT_EXPERIENCE_VARIANTS.VARIANT_A;
  if (phone && anyConsentN) return WEB_PROMPT_EXPERIENCE_VARIANTS.VARIANT_B;
  if (!phone && allConsentsY) return WEB_PROMPT_EXPERIENCE_VARIANTS.VARIANT_C;
  if (phone && allConsentsY) return WEB_PROMPT_EXPERIENCE_VARIANTS.VARIANT_D;
  return '';
}
// Utility to generate consent datalayer string for WebPrompt flows

type ConsentStatus = {
  phoneExists: 'Y' | 'N';
  emailConsent: 'Y' | 'N';
  smsConsent: 'Y' | 'N';
  postalConsent: 'Y' | 'N';
};

export function getConsentDataLayerString({
  phoneExists,
  emailConsent,
  smsConsent,
  postalConsent,
}: ConsentStatus): string {
  return `Phone Exists:${phoneExists}|Email Consent:${emailConsent}|SMS Consent:${smsConsent}|Postal Consent:${postalConsent}`;
}

export function getConsentStatusFromOptions(
  communicationOptions: ConsentOptionPayload[] = []
): Omit<ConsentStatus, 'phoneExists'> {
  let emailConsent: 'Y' | 'N' = 'N',
    smsConsent: 'Y' | 'N' = 'N',
    postalConsent: 'Y' | 'N' = 'N';
  communicationOptions.forEach(opt => {
    // Email consent: support both EML/PRGRM (used in COMMUNICATION_SUBSCRIBED) and EMAIL/MRKT (possible backend variant)
    if (
      (opt.method?.code === 'EML' && opt.type?.code === 'PRGRM') ||
      (opt.method?.code === 'EMAIL' && opt.type?.code === 'MRKT')
    ) {
      emailConsent = opt.subscribed ? 'Y' : 'N';
    }
    if (opt.method?.code === 'SMS' && opt.type?.code === 'MRKT') smsConsent = opt.subscribed ? 'Y' : 'N';
    if (opt.method?.code === 'POST' && opt.type?.code === 'GNRL') postalConsent = opt.subscribed ? 'Y' : 'N';
  });
  return { emailConsent, smsConsent, postalConsent };
}

// Helper to determine if phone exists (type MT only)
interface Customer {
  contactInformation?: {
    phones?: Array<{ number?: string; type?: { code?: string } }>;
  };
}
export function getPhoneExistsFromCustomer(customer: Customer): 'Y' | 'N' {
  const phones = customer?.contactInformation?.phones || [];
  return phones.some(
    (phone: { number?: string; type?: { code?: string } }) => !!phone?.number && phone?.type?.code === 'MT'
  )
    ? 'Y'
    : 'N';
}
